import api from "./apiClient"
import {
  PostsResponseSchema,
  PostMessagesSchema,
  type PostsResponse,
  type PostMessages,
  type Post,
} from "@features/dashboard/models/posts"

export const POSTS_PER_PAGE = 25

export const getPosts = async (
  page: number,
  tags: number[] = [],
  query = "",
  type?: number,
  postId?: number
): Promise<PostsResponse> => {
  const formData = new FormData()
  formData.set("page", String(page))
  formData.set("per_page", String(POSTS_PER_PAGE))
  for (const t of tags) formData.append("tag_id[]", String(t))
  if (query) formData.set("query", query)
  if (type !== undefined) formData.set("type", String(type))
  if (postId) formData.set("post_id", String(postId))

  const url = postId ? "get-post" : "get-posts"
  const { data } = await api.post(`/posts/${url}`, formData)
  return PostsResponseSchema.parse(data)
}

export const getPostMessages = async (postId: number): Promise<PostMessages> => {
  const { data } = await api.get(`/posts/get-messages?id=${postId}`)
  return PostMessagesSchema.parse(data)
}

export const likePost = async (postId: number): Promise<void> => {
  await api.post(`/posts/like?id=${postId}`)
}

export const deletePost = async (postId: number): Promise<void> => {
  await api.post(`/posts/delete-post?id=${postId}`)
}

export const updatePost = async (postId: number, content: string): Promise<Post> => {
  const formData = new FormData()
  formData.append("content", content)
  const { data } = await api.post(`/posts/update-post?id=${postId}`, formData)
  return data.post
}

export const moderatePost = async (postId: number): Promise<Post> => {
  const { data } = await api.get(`/posts/moderate-post?id=${postId}&accept=true`)
  return data.post
}

export const newComment = async (
  postId: number,
  parentId: number,
  content: string
): Promise<void> => {
  const formData = new FormData()
  formData.append("post_id", String(postId))
  formData.append("parent_id", String(parentId))
  formData.append("content", content)
  await api.post("/posts/new-comment", formData)
}

export const updateComment = async (commentId: number, content: string): Promise<void> => {
  const formData = new FormData()
  formData.append("content", content)
  await api.post(`/posts/update-comment?id=${commentId}`, formData)
}

export const deleteComment = async (commentId: number): Promise<void> => {
  await api.post(`/posts/delete-comment?id=${commentId}`)
}

export const moderateComment = async (commentId: number): Promise<void> => {
  await api.get(`/posts/moderate-comment?id=${commentId}&accept=true`)
}

export const getTags = async (type?: number): Promise<any[]> => {
  const params = type !== undefined ? `?type=${type}` : ""
  const { data } = await api.get(`/posts/get-tags${params}`)
  return data
}

export const addTagToPost = async (postId: number, tagId: number): Promise<void> => {
  const formData = new FormData()
  formData.append("post_id", String(postId))
  formData.append("tag_id", String(tagId))
  await api.post("/posts/add-tag", formData)
}

export const removeTagFromPost = async (postId: number, tagId: number): Promise<void> => {
  const formData = new FormData()
  formData.append("post_id", String(postId))
  formData.append("tag_id", String(tagId))
  await api.post("/posts/delete-tag", formData)
}

export const createPost = async (
  formData: FormData
): Promise<{ status: string; errors?: Record<string, string> }> => {
  const { data } = await api.post("/posts/new-post", formData)
  return data
}

export default {
  getPosts,
  getPostMessages,
  likePost,
  deletePost,
  updatePost,
  moderatePost,
  newComment,
  updateComment,
  deleteComment,
  moderateComment,
  getTags,
  addTagToPost,
  removeTagFromPost,
  createPost,
}